import { Card, Col, Empty, Row, Space, Typography } from 'antd';
import { CommentOutlined, LaptopOutlined } from '@ant-design/icons';
import { Counters } from './components/Counters';
import { ForumsList } from './components/ForumsList';
import { GeneralInfo } from './components/GeneralInfo';
import { InteractiveClassList } from './components/InteractiveClassList';
import { isClassroomForumEnabled } from 'services/tenants';
import { useCanMultiple } from 'components/layout/sider';
import type { IProfessorAreaClassroom } from 'interfaces/professor_area_classrooms';

interface TabGeneralProps {
  data: IProfessorAreaClassroom;
}

export const TabGeneral = ({ data }: TabGeneralProps) => {
  const [
    interactiveClassesCan,
    forumsCan,
    threadsCan,
    assignmentsCan,
    assessmentsCan
  ] = useCanMultiple([
    { resource: 'professor_area/interactive_classes', action: 'list' },
    { resource: 'professor_area/forums', action: 'list' },
    { resource: 'professor_area/discussion_threads', action: 'list' },
    { resource: 'professor_area/classroom_assignments', action: 'list' },
    { resource: 'professor_area/classroom_assessments', action: 'list' }
  ]);

  const classroomForumEnabled = isClassroomForumEnabled();
  const canSeeInteractiveClasses = interactiveClassesCan.data?.can ?? false;
  const canSeeForums = forumsCan.data?.can ?? false;
  const canAccessThreads = threadsCan.data?.can ?? false;
  const canAccessAssignments = assignmentsCan.data?.can ?? false;
  const canAccessEvaluations =
    (assessmentsCan.data?.can ?? false) || canAccessAssignments;

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} sm={24} xl={8}>
        <GeneralInfo data={data} />
      </Col>

      <Col xs={24} sm={24} xl={16}>
        <Counters
          assessmentsCount={data.pending_evaluation_assessment_attempts_count}
          assignmentsCount={data.pending_evaluation_assignment_attempts_count}
          threadsCount={data.pending_faq_professors_count}
          canAccessThreads={canAccessThreads}
          canAccessAssignments={canAccessAssignments}
          canAccessEvaluations={canAccessEvaluations}
        />

        <Space direction="vertical" size="large" className="w-full flex-1">
          {canSeeInteractiveClasses && <InteractiveClassList />}
          {!canSeeInteractiveClasses && (
            <Card
              title={
                <Space className="!items-center">
                  <LaptopOutlined className="text-xl text-neutral-400" />
                  <Typography.Text className="text-md font-semibold">
                    Próximas aulas interativas
                  </Typography.Text>
                </Space>
              }
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sem permissão para visualizar aulas interativas."
              />
            </Card>
          )}

          {classroomForumEnabled && canSeeForums && <ForumsList />}
          {classroomForumEnabled && !canSeeForums && (
            <Card
              title={
                <Space className="!items-center">
                  <CommentOutlined className="text-xl text-neutral-400" />
                  <Typography.Text className="text-md font-semibold">
                    Fórum da turma
                  </Typography.Text>
                </Space>
              }
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sem permissão para visualizar o fórum da turma."
              />
            </Card>
          )}
        </Space>
      </Col>
    </Row>
  );
};
